import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api';

interface FeatureFlags {
  email: boolean;
  sms: boolean;
  free_trial: boolean;
  require_card_for_trial: boolean;
  payments: boolean;
  analytics: boolean;
  campaigns: boolean;
  templates: boolean;
  sequences: boolean;
}

interface FeatureFlagContextType {
  features: FeatureFlags;
  isLoading: boolean;
  error: Error | null;
  isSmsEnabled: boolean;
  isEmailEnabled: boolean;
  isFreeTrialEnabled: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

const CACHE_KEY = 'feature_flags';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const fetchFeatures = async (): Promise<FeatureFlags> => {
  const response = await apiClient.get('/api/v1/system/features');
  return response.data;
};

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cachedFeatures, setCachedFeatures] = useState<FeatureFlags | null>(null);
  const [cacheTimestamp, setCacheTimestamp] = useState<number | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['features'],
    queryFn: fetchFeatures,
    staleTime: CACHE_TTL,
    cacheTime: CACHE_TTL,
    retry: 3,
    retryDelay: 1000,
  });

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      const timestamp = localStorage.getItem(`${CACHE_KEY}_timestamp`);
      
      if (cached && timestamp) {
        const parsedFeatures = JSON.parse(cached);
        const parsedTimestamp = parseInt(timestamp);
        
        // Check if cache is still valid
        if (Date.now() - parsedTimestamp < CACHE_TTL) {
          setCachedFeatures(parsedFeatures);
          setCacheTimestamp(parsedTimestamp);
        }
      }
    } catch (error) {
      console.warn('Failed to load cached feature flags:', error);
    }
  }, []);

  // Update cache when new data arrives
  useEffect(() => {
    if (data) {
      setCachedFeatures(data);
      setCacheTimestamp(Date.now());
      
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        localStorage.setItem(`${CACHE_KEY}_timestamp`, Date.now().toString());
      } catch (error) {
        console.warn('Failed to cache feature flags:', error);
      }
    }
  }, [data]);

  const features = cachedFeatures || data || {
    email: true,
    sms: false,
    free_trial: true,
    require_card_for_trial: false,
    payments: true,
    analytics: true,
    campaigns: true,
    templates: true,
    sequences: true,
  };

  const contextValue: FeatureFlagContextType = {
    features,
    isLoading,
    error: error as Error | null,
    isSmsEnabled: features.sms,
    isEmailEnabled: features.email,
    isFreeTrialEnabled: features.free_trial,
  };

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

// Convenience hook for SMS feature
export const useSmsFeature = (): boolean => {
  const { isSmsEnabled } = useFeatureFlags();
  return isSmsEnabled;
};

// Convenience hook for email feature
export const useEmailFeature = (): boolean => {
  const { isEmailEnabled } = useFeatureFlags();
  return isEmailEnabled;
};

// Convenience hook for free trial feature
export const useFreeTrialFeature = (): boolean => {
  const { isFreeTrialEnabled } = useFeatureFlags();
  return isFreeTrialEnabled;
};
